import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { TABLE_STATUS, tableStatusLabel } from '../../lib/constants';
import { cn } from '../../lib/utils';
import { useRealtime } from '../../lib/realtime';
import { Users2 } from 'lucide-react';

export default function WaiterTables() {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [zones, setZones] = useState([]);
  // Sala attiva: ogni sala ha la sua schermata (come nel profilo admin).
  const [activeZone, setActiveZone] = useState(null);
  const navigate = useNavigate();

  const loadTables = async () => { const { data } = await api.get('/tables'); setTables(data); };
  const loadZones = async () => {
    const { data } = await api.get('/zones');
    setZones(data || []);
    setActiveZone((prev) => (prev && (data || []).find((z) => z.id === prev)) ? prev : ((data || [])[0]?.id || null));
  };
  const load = async () => { await Promise.all([loadZones(), loadTables()]); };

  useEffect(() => { load(); const tm = setInterval(loadTables, 10000); return () => clearInterval(tm); }, []);

  useRealtime((msg) => {
    if (msg && (msg.event === 'tables' || msg.event === 'orders' || msg.event === 'payments')) loadTables();
  });

  const open = (tbl) => {
    // Do NOT open an order yet: navigate to the order page, which creates the
    // order lazily only when the first product is added (keeps free tables free).
    navigate(`/waiter/order/${tbl.id}`);
  };

  // Un tavolo appartiene alla sala attiva se ha lo stesso zone_id, oppure se è
  // senza sala e stiamo guardando la prima sala (stessa logica del profilo admin).
  const inActiveZone = (tbl) => tbl.zone_id === activeZone || (!tbl.zone_id && activeZone === zones[0]?.id);
  const zoneTables = useMemo(
    () => tables.filter((tbl) => !tbl.merged_hidden && (zones.length === 0 || inActiveZone(tbl))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables, activeZone, zones],
  );

  return (
    <div className="p-4">
      <h1 className="font-display text-xl font-semibold mb-1">{t('waiter.tables')}</h1>
      <p className="text-sm text-muted-foreground mb-4">{t('waiter.tapTable')}</p>

      {/* Schede sala: ogni sala ha la sua pagina (mostrate solo se c'è più di una sala). */}
      {zones.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-4" data-testid="waiter-zone-tabs">
          {zones.map((z) => (
            <button
              key={z.id}
              onClick={() => setActiveZone(z.id)}
              data-testid="waiter-zone-tab"
              aria-pressed={activeZone === z.id}
              className={cn(
                'h-10 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors',
                activeZone === z.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-secondary',
              )}
            >
              {z.name}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="waiter-tables-list">
        {zoneTables.map((tbl) => {
          const st = TABLE_STATUS[tbl.status] || TABLE_STATUS.free;
          return (
            <button key={tbl.id} data-testid="waiter-open-table-button" onClick={() => open(tbl)}
              className={cn('h-28 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 active:scale-[0.98] transition-transform')}
              style={{ backgroundColor: `hsl(var(--${st.color}) / 0.12)`, borderColor: `hsl(var(--${st.color}))` }}>
              <span className="font-display font-semibold text-lg" style={{ color: `hsl(var(--${st.color}))` }}>{tbl.name}</span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Users2 className="h-3 w-3" /> {tbl.seats}</span>
              <span className="text-xs font-medium" style={{ color: `hsl(var(--${st.color}))` }}>{tableStatusLabel(t, tbl.status)}</span>
            </button>
          );
        })}
        {zoneTables.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-12 text-sm" data-testid="waiter-tables-empty">
            {t('waiter.noTablesInRoom', { defaultValue: 'Nessun tavolo in questa sala' })}
          </div>
        )}
      </div>
    </div>
  );
}
